/* eslint-disable react-doctor/only-export-components */
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../../lib/utils.ts";

const primaryButtonClass =
    "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[inset_0_0_0_1px_#ffb02d]";
const outlineButtonClass =
    "border border-border bg-true-white hover:bg-black/3 hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50";
const ghostButtonClass =
    "hover:bg-true-black/4 hover:text-accent-foreground dark:hover:bg-accent/50";
const svgInset = "has-[>svg:first-child]:pl-3.5 has-[>svg:last-child]:pr-3.5";

const buttonVariants = cva(
    "inline-flex touch-manipulation cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-nested font-family-body text-[13px] font-medium leading-[130%] tracking-[-0.32px] transition-[color,background-color,box-shadow,border-color,transform,opacity] [-webkit-tap-highlight-color:transparent] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive active:scale-[0.98] active:duration-100 motion-reduce:active:scale-100",
    {
        variants: {
            variant: {
                default: primaryButtonClass,
                destructive:
                    "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
                outline: outlineButtonClass,
                "outline-ink": `${outlineButtonClass} text-doberman-black`,
                secondary: "border border-border bg-[#FFFFFF] text-[#171717]",
                dark: "bg-doberman-black text-white hover:bg-doberman-black/80 focus-visible:ring-doberman-black/70",
                muted: "bg-true-black/4 text-doberman-black/80 hover:bg-true-black/4 hover:text-accent-foreground text-cta",
                ghost: ghostButtonClass,
                link: "text-primary underline-offset-4 hover:underline active:scale-100",
                bottom: `fixed bottom-[calc(2.5rem_+_env(safe-area-inset-bottom))] left-[calc(1.75rem_+_env(safe-area-inset-left))] right-[calc(1.75rem_+_env(safe-area-inset-right))] ${primaryButtonClass} text-black disabled:bg-[#C97E00] disabled:opacity-40 disabled:cursor-not-allowed`,
                cream: "bg-havanese text-doberman-black hover:bg-maltese-white focus-visible:ring-maltese-white",
                "ghost-inverse":
                    "text-maltese-white/75 hover:bg-maltese-white/10 hover:text-maltese-white focus-visible:ring-maltese-white",
                text: "text-doberman-black hover:bg-transparent hover:text-doberman-black/70 focus-visible:ring-doberman-black",
            },
            emphasis: {
                default: "",
                cta: "text-cta",
                lead: "text-body font-semibold",
            },
            size: {
                default: `h-9 px-4 py-2 ${svgInset}`,
                touch: `h-11 px-4 py-2 ${svgInset}`,
                "primary-action": `h-12 px-4 py-2 ${svgInset}`,
                wide: `h-11 px-8 py-2 ${svgInset}`,
                "touch-narrow": `h-11 min-w-11 px-2 py-2 sm:px-4 ${svgInset}`,
                sm: "h-8 gap-1.5 px-3 has-[>svg:first-child]:pl-2.5 has-[>svg:last-child]:pr-2.5",
                lg: "h-10 px-6 has-[>svg:first-child]:pl-5 has-[>svg:last-child]:pr-5",
                icon: "size-9",
                "icon-sm": "size-8",
                "icon-lg": "size-10",
                bottom: "h-11",
                "touch-text": "h-11 px-3 -mx-3 text-cta",
            },
        },
        defaultVariants: {
            variant: "default",
            emphasis: "default",
            size: "default",
        },
    },
);

function Button({
    className,
    variant = "default",
    emphasis = "default",
    size = "default",
    asChild = false,
    ...props
}: React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
    }) {
    const Comp = asChild ? Slot : "button";

    return (
        <Comp
            data-slot="button"
            data-variant={variant}
            data-size={size}
            className={cn(
                buttonVariants({ variant, emphasis, size, className }),
            )}
            {...props}
        />
    );
}

export { Button, buttonVariants };
